import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/pageTitle';
import { 
  Home, 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Globe,
  TrendingUp,
  BarChart3
} from 'lucide-react';
import { useAuth } from 'contexts/AuthContext';
import { useToast } from 'contexts/ToastContext';
import {
  getLegalTranslations,
  getLegalTranslationsInternal,
  getLegalTranslationsByProsecutorID,
  getLegalTranslationsInternalByProsecutor
} from 'services/legalTranslationService';
import {
  SECTOR_DIRECTIVES_REQUEST_STATUS
} from 'utils/constantValues';

// Helper function to count items by status
const countByStatus = (items: any[], statusObj: any) => {
  const counts: { [key: string]: number } = {};
  Object.keys(statusObj).forEach(statusKey => {
    counts[statusObj[statusKey].label] = 0;
  });

  items.forEach(item => {
    const statusValue = item.status || item.current_status || item.request_status;
    Object.keys(statusObj).forEach(statusKey => {
      if (statusObj[statusKey].value === statusValue) {
        counts[statusObj[statusKey].label]++;
      }
    });
  });

  return counts;
};

// Helper to filter data by date range
const filterByDateRange = (items: any[], startDate: Date, endDate: Date) => {
  return items.filter(item => {
    const created_at = item.created_at || item.createdDate;
    if (!created_at) return false;
    const itemDate = new Date(created_at);
    return itemDate >= startDate && itemDate < endDate;
  });
};

// Helper functions to categorize status labels
const isActiveStatus = (label: string | undefined | null): boolean => {
  if (!label) return false;
  const activeKeywords = [
    'received', 'pending', 'underway', 'in progress', 'under review',
    'under analysis', 'submitted', 'submitted by stakeholder', 'submitted internally',
    'oral instruction logged', 'proposal submitted', 'proposal underway',
    'draft prepared', 'team appointed', 'identification in progress',
    'audit in progress', 'summary submitted', 'session scheduled',
    'team assigned', 'training manual submitted', 'training scheduled',
    'assigned to prosecutor', 'forwarded to', 'sent to'
  ];
  const lowerLabel = label.toLowerCase();
  return activeKeywords.some(keyword => lowerLabel.includes(keyword));
};

const isCompletedStatus = (label: string | undefined | null): boolean => {
  if (!label) return false;
  const completedKeywords = [
    'completed', 'published', 'final', 'archived', 'closed',
    'finalization complete', 'audit complete', 'final approval',
    'reference number submitted', 'letter reference number submitted'
  ];
  const lowerLabel = label.toLowerCase();
  return completedKeywords.some(keyword => lowerLabel.includes(keyword));
};

const isPendingReviewStatus = (label: string | undefined | null): boolean => {
  if (!label) return false;
  const pendingKeywords = [
    'revision', 'revision requested', 'revision updated', 'feedback',
    'awaiting approval', 'draft approved', 'proposal revision',
    'proposal update', 'draft - updated', 'proposal presentation feedback submitted',
    'additional info', 'waiting for', 'back to', 'reviewed by',
    'approved by head', 'follow-up'
  ];
  const lowerLabel = label.toLowerCase();
  return pendingKeywords.some(keyword => lowerLabel.includes(keyword));
};

// Calculate statistics from service data for a given period
const calculateStatsForPeriod = (data: any[], startDate: Date, endDate: Date) => {
  let active = 0;
  let completed = 0;
  let pendingReview = 0;
  
  if (data && data.length > 0) {
    const periodData = filterByDateRange(data, startDate, endDate);
    if (periodData.length > 0) {
      const statusCounts = countByStatus(periodData, SECTOR_DIRECTIVES_REQUEST_STATUS);
      Object.entries(statusCounts).forEach(([label, count]) => {
        const countNum = count as number;
        if (isActiveStatus(label)) {
          active += countNum;
        } else if (isCompletedStatus(label)) {
          completed += countNum;
        } else if (isPendingReviewStatus(label)) {
          pendingReview += countNum;
        }
      });
    }
  }
  
  return { active, completed, pendingReview };
};

// Get date range for a month
const getMonthRange = (date: Date): { start: Date; end: Date } => {
  const year = date.getFullYear();
  const month = date.getMonth();
  return {
    start: new Date(year, month, 1),
    end: new Date(year, month + 1, 1)
  };
};

// Calculate percentage change
const calculateChange = (current: number, previous: number): string => {
  if (previous === 0) return current > 0 ? '+100%' : '0%';
  const change = ((current - previous) / previous) * 100;
  return `${change >= 0 ? '+' : ''}${change.toFixed(0)}%`;
};

const LegalTranslationProsecutorDashboard = () => {
  const { t } = useTranslation();
  const contextData = useAuth();
  const { showToast } = useToast();

  // State for legal translation data
  const [translationData, setTranslationData] = useState<{
    external: any[];
    internal: any[];
    combined: any[];
    loading: boolean;
  }>({
    external: [],
    internal: [],
    combined: [],
    loading: true
  });

  // Fetch assigned legal translation data on component mount
  useEffect(() => {
    const fetchTranslationData = async () => {
      try {
        const prosecutorUserId = contextData.user?.user_id || contextData.user?.id;
        if (!prosecutorUserId) {
          showToast(t("NoUserInfo") || "Unable to identify user", "error");
          setTranslationData(prev => ({ ...prev, loading: false }));
          return;
        }

        // Fetch both external and internal translations assigned to this prosecutor
        const [externalData, internalData] = await Promise.allSettled([
          getLegalTranslationsByProsecutorID(prosecutorUserId.toString()),
          getLegalTranslationsInternalByProsecutor(prosecutorUserId.toString())
        ]);
        
        const externalItems = externalData.status === 'fulfilled' ? externalData.value : [];
        const internalItems = internalData.status === 'fulfilled' ? internalData.value : [];
        
        const combinedData = [...externalItems, ...internalItems];
        
        setTranslationData({
          external: externalItems as any[],
          internal: internalItems as any[],
          combined: combinedData as any[],
          loading: false
        });
      } catch (error) {
        console.error('Error fetching legal translation data:', error);
        showToast(t("ErrorLoadingData") || "Error loading data", "error");
        setTranslationData(prev => ({ ...prev, loading: false }));
      }
    };

    fetchTranslationData();
  }, [contextData.user, showToast, t]);

  // Calculate totals
  const totalExternal = translationData.external.length;
  const totalInternal = translationData.internal.length;
  const totalTranslations = totalExternal + totalInternal;

  // Calculate month-over-month statistics
  const isLoading = translationData.loading;
  const now = new Date();
  const currentMonthRange = getMonthRange(now);
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthRange = getMonthRange(lastMonthDate);

  const currentMonthStats = isLoading ? { active: 0, completed: 0, pendingReview: 0 } : 
    calculateStatsForPeriod(translationData.combined, currentMonthRange.start, currentMonthRange.end);
  const lastMonthStats = isLoading ? { active: 0, completed: 0, pendingReview: 0 } : 
    calculateStatsForPeriod(translationData.combined, lastMonthRange.start, lastMonthRange.end);

  const activeTasks = currentMonthStats.active;
  const completedTasks = currentMonthStats.completed;
  const pendingReviewTasks = currentMonthStats.pendingReview;

  const activeChange = calculateChange(currentMonthStats.active, lastMonthStats.active);
  const completedChange = calculateChange(currentMonthStats.completed, lastMonthStats.completed);
  const pendingChange = calculateChange(currentMonthStats.pendingReview, lastMonthStats.pendingReview);

  // Get status counts for combined data
  const statusCounts = isLoading ? {} : countByStatus(translationData.combined, SECTOR_DIRECTIVES_REQUEST_STATUS);

  // Stats for the dashboard
  const stats = [
    {
      title: t("TotalTranslations") || "Total Translations",
      value: isLoading ? '...' : totalTranslations.toString(),
      change: calculateChange(currentMonthStats.active + currentMonthStats.completed + currentMonthStats.pendingReview, 
                            lastMonthStats.active + lastMonthStats.completed + lastMonthStats.pendingReview),
      changeType: 'positive' as const,
      icon: <Globe className="w-6 h-6" />,
      bgColor: 'bg-teal-50',
      iconColor: 'text-teal-600',
    },
    {
      title: t("WaitingForAssignment") || "Waiting for Assignment",
      value: isLoading ? '...' : activeTasks.toString(),
      change: activeChange,
      changeType: 'positive' as const,
      icon: <Clock className="w-6 h-6" />,
      bgColor: 'bg-yellow-50',
      iconColor: 'text-yellow-600',
    },
    {
      title: t("Completed") || "Completed",
      value: isLoading ? '...' : completedTasks.toString(),
      change: completedChange,
      changeType: 'positive' as const,
      icon: <CheckCircle className="w-6 h-6" />,
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
    },
    {
      title: t("PendingReview") || "Pending Review",
      value: isLoading ? '...' : pendingReviewTasks.toString(),
      change: pendingChange,
      changeType: 'negative' as const,
      icon: <AlertCircle className="w-6 h-6" />,
      bgColor: 'bg-red-50',
      iconColor: 'text-red-600',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <PageTitle
        text={t("LegalTranslationDashboard") || "Legal Translation Dashboard"}
        icon={<Home />}
        iconBgColor="bg-gray-100"
        iconColor="text-gray-800"
        subtitle={t("LegalTranslationDashboardSubtitle") || "Overview of all legal translation requests and their current status"}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                <p className={`text-sm mt-1 ${
                  stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {t("ChangeFromLastMonth", { change: stat.change })}
                </p>
              </div>
              <div className={`p-3 rounded-full ${stat.bgColor}`}>
                <span className={stat.iconColor}>{stat.icon}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Translation Overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{t("TranslationOverview") || "Translation Overview"}</h3>
          <p className="text-sm text-gray-600 mt-1">{t("TranslationOverviewSubtitle") || "Breakdown by request type and status"}</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* External Translations */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <Globe className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{t("ExternalTranslations") || "External Translations"}</h4>
                  <p className="text-sm text-gray-600">{t("FromStakeholders") || "From external stakeholders"}</p>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-3xl font-bold text-gray-900">
                  {isLoading ? '...' : totalExternal}
                </span>
                <span className="text-sm text-gray-500">{t("requests")}</span>
              </div>
              {!isLoading && totalExternal > 0 && (
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  {Object.entries(statusCounts).map(([status, count]) => {
                    if ((count as number) > 0) {
                      return (
                        <span key={status} className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {`${status}: ${count}`}
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>
              )}
            </div>

            {/* Internal Translations */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-purple-100 p-2 rounded-lg">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{t("InternalTranslations") || "Internal Translations"}</h4>
                  <p className="text-sm text-gray-600">{t("FromInternalUnits") || "From internal departments"}</p>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-3xl font-bold text-gray-900">
                  {isLoading ? '...' : totalInternal}
                </span>
                <span className="text-sm text-gray-500">{t("requests")}</span>
              </div>
              {!isLoading && totalInternal > 0 && (
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  {Object.entries(statusCounts).map(([status, count]) => {
                    if ((count as number) > 0) {
                      return (
                        <span key={status} className="bg-purple-100 text-purple-800 px-2 py-1 rounded">
                          {`${status}: ${count}`}
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalTranslationProsecutorDashboard;
